import { useReducer, useCallback } from 'react';

/**
 * Modal state reducer - manages all modal states together
 */
const modalReducer = (state, action) => {
    switch (action.type) {
        case 'OPEN_ARV_DESCRIPTION':
            return { ...state, arvDescription: true };
        case 'CLOSE_ARV_DESCRIPTION':
            return { ...state, arvDescription: false };
        case 'OPEN_COMP_TYPE':
            return { ...state, compType: true };
        case 'CLOSE_COMP_TYPE':
            return { ...state, compType: false };
        case 'OPEN_MISSING_COMP':
            return { ...state, missingComp: true };
        case 'CLOSE_MISSING_COMP':
            return { ...state, missingComp: false };
        case 'OPEN_TOGGLE_COMP':
            return { ...state, toggleComp: true };
        case 'CLOSE_TOGGLE_COMP':
            return { ...state, toggleComp: false };
        case 'OPEN_REGENERATE_ARV':
            return { ...state, regenerateARV: true };
        case 'CLOSE_REGENERATE_ARV':
            return { ...state, regenerateARV: false };
        case 'OPEN_RE_RUN_REPORT':
            return { ...state, reRunReport: true };
        case 'CLOSE_RE_RUN_REPORT':
            return { ...state, reRunReport: false };
        case 'OPEN_BASEMENT_MODAL':
            return { ...state, basementModal: true };
        case 'CLOSE_BASEMENT_MODAL':
            return { ...state, basementModal: false };
        case 'CLOSE_ALL':
            return {
                arvDescription: false,
                compType: false,
                missingComp: false,
                toggleComp: false,
                regenerateARV: false,
                reRunReport: false,
                basementModal: false
            };
        default:
            return state;
    }
};

/**
 * Custom hook for managing all modal states
 */
export const useModals = () => {
    const [modals, dispatch] = useReducer(modalReducer, {
        arvDescription: false,
        compType: false,
        missingComp: false,
        toggleComp: false,
        regenerateARV: false,
        reRunReport: false,
        basementModal: false
    });

    const openArvDescription = useCallback(() => dispatch({ type: 'OPEN_ARV_DESCRIPTION' }), []);
    const closeArvDescription = useCallback(() => dispatch({ type: 'CLOSE_ARV_DESCRIPTION' }), []);
    const openCompType = useCallback(() => dispatch({ type: 'OPEN_COMP_TYPE' }), []);
    const closeCompType = useCallback(() => dispatch({ type: 'CLOSE_COMP_TYPE' }), []);
    const openMissingComp = useCallback(() => dispatch({ type: 'OPEN_MISSING_COMP' }), []);
    const closeMissingComp = useCallback(() => dispatch({ type: 'CLOSE_MISSING_COMP' }), []);
    const openToggleComp = useCallback(() => dispatch({ type: 'OPEN_TOGGLE_COMP' }), []);
    const closeToggleComp = useCallback(() => dispatch({ type: 'CLOSE_TOGGLE_COMP' }), []);
    const openRegenerateARV = useCallback(() => dispatch({ type: 'OPEN_REGENERATE_ARV' }), []);
    const closeRegenerateARV = useCallback(() => dispatch({ type: 'CLOSE_REGENERATE_ARV' }), []);
    const openReRunReport = useCallback(() => dispatch({ type: 'OPEN_RE_RUN_REPORT' }), []);
    const closeReRunReport = useCallback(() => dispatch({ type: 'CLOSE_RE_RUN_REPORT' }), []);
    const openBasementModal = useCallback(() => dispatch({ type: 'OPEN_BASEMENT_MODAL' }), []);
    const closeBasementModal = useCallback(() => dispatch({ type: 'CLOSE_BASEMENT_MODAL' }), []);
    const closeAllModals = useCallback(() => dispatch({ type: 'CLOSE_ALL' }), []);

    return {
        modals,
        openArvDescription,
        closeArvDescription,
        openCompType,
        closeCompType,
        openMissingComp,
        closeMissingComp,
        openToggleComp,
        closeToggleComp,
        openRegenerateARV,
        closeRegenerateARV,
        openReRunReport,
        closeReRunReport,
        openBasementModal,
        closeBasementModal,
        closeAllModals
    };
};

/**
 * Image viewer state reducer
 */
const imageViewerReducer = (state, action) => {
    switch (action.type) {
        case 'SHOW_SCROLLABLE_IMAGES':
            return {
                ...state,
                viewScrollableImages: true,
                imageViewProperty: action.property
            };
        case 'CLOSE_SCROLLABLE_IMAGES':
            return {
                ...state,
                viewScrollableImages: false
            };
        case 'OPEN_IMAGE_GALLERY':
            return {
                ...state,
                viewImageGallery: true,
                viewScrollableImages: false,
                imageViewPropertyIndex: action.index,
                imageGalleryOpenedFromRepairCosts: action.fromRepairCosts || false
            };
        case 'CLOSE_IMAGE_GALLERY':
            return {
                ...state,
                viewImageGallery: false,
                viewScrollableImages: action.restoreScrollable || false,
                imageViewPropertyIndex: 0,
                imageGalleryOpenedFromRepairCosts: false
            };
        case 'SET_IMAGE_INDEX':
            return {
                ...state,
                imageViewPropertyIndex: action.index
            };
        case 'SET_IMAGE_VIEW_PROPERTY':
            return {
                ...state,
                imageViewProperty: action.property
            };
        default:
            return state;
    }
};

/**
 * Custom hook for managing image viewer state
 */
export const useImageViewer = () => {
    const [state, dispatch] = useReducer(imageViewerReducer, {
        viewImageGallery: false,
        viewScrollableImages: false,
        imageViewProperty: null,
        imageViewPropertyIndex: 0,
        imageGalleryOpenedFromRepairCosts: false
    });

    const showScrollableImages = useCallback((property) => {
        dispatch({ type: 'SHOW_SCROLLABLE_IMAGES', property });
        window.scrollTo({ top: 0, behavior: "smooth" });
        // recordEvent("view_scrollable_images", {});
    }, []);

    const closeScrollableImages = useCallback(() => {
        dispatch({ type: 'CLOSE_SCROLLABLE_IMAGES' });
        // recordEvent("close_scrollable_images", {});
    }, []);

    const openImageGallery = useCallback((index, fromRepairCosts = false) => {
        dispatch({ type: 'OPEN_IMAGE_GALLERY', index, fromRepairCosts });
        window.scrollTo({ top: 0, behavior: "smooth" });
        // recordEvent("open_image_gallery", {});
    }, []);

    const closeImageGallery = useCallback((restoreScrollable = false) => {
        dispatch({ type: 'CLOSE_IMAGE_GALLERY', restoreScrollable });
        // recordEvent("close_image_gallery", {});
    }, []);

   const setImageIndex = useCallback((index) => {
       dispatch({ type: 'SET_IMAGE_INDEX', index });
   }, []);

   return {
       ...state,
       showScrollableImages,
       closeScrollableImages,
       openImageGallery,
       closeImageGallery,
       setImageIndex
   };
};

